/**
 * Dashboard — pure view-model helpers.
 *
 * Everything in here is side-effect free so the tricky parts of the page (the
 * notify gate, which is a CROSS-PAYLOAD join, and the client-side filter) can be
 * reasoned about — and later tested — without React or the network.
 *
 * The KPI counters intentionally read `DashboardSummary` instead of
 * recomputing anything client-side: `_summarise()` in
 * app/services/dashboard_service.py runs AFTER the `only_affected` filter, so
 * every tile describes the payload the server just sent, not the whole CMDB.
 */

import { toneForCount } from '../../lib/format';
import type {
  BenachrichtigungBlock,
  DashboardSummary,
  IsoDateTime,
  KpiCounter,
  OverviewRow,
  VulnerabilityEventRow,
} from '../../lib/types';

/** Auto-refresh period, matching the 60 s of the vanilla dashboard. */
export const AUTO_REFRESH_MS = 60_000;

/** Trimmed value or a German fallback — never an empty cell. */
export function orFallback(value: string | null | undefined, fallback: string): string {
  const text = (value ?? '').trim();
  return text === '' ? fallback : text;
}

/* ------------------------------------------------------------------------- *
 * KPI tiles
 * ------------------------------------------------------------------------- */

/**
 * The five counters of the KPI strip, the action counter first. "Produkte"
 * and "Betroffene Komponenten" are left out on purpose: the grouped table
 * answers them per Kunde/Produkt, and the strip should only carry the numbers
 * that call for action.
 *
 * @param offeneBewertungen - `VulnerabilityEventsResponse.offen`; the only tile
 *   that does not come from the overview payload.
 */
export function kpiCounters(summary: DashboardSummary, offeneBewertungen: number): KpiCounter[] {
  return [
    {
      label: 'Zu benachrichtigen',
      value: summary.affected_not_notified,
      tone: toneForCount(summary.affected_not_notified, 'warn'),
      title: 'Betroffene Auslieferungen, für die noch keine Benachrichtigung versendet wurde.',
    },
    {
      label: 'Betroffene Kunden',
      value: summary.customers_affected,
      tone: toneForCount(summary.customers_affected),
      title: 'Kunden mit mindestens einem betroffenen Produkt.',
    },
    {
      label: 'Betroffene Auslieferungen',
      value: summary.products_affected,
      tone: toneForCount(summary.products_affected),
      title: 'Produktinstanzen mit mindestens einer KEV-betroffenen Komponentenversion.',
    },
    {
      label: 'KEV-CVEs',
      value: summary.kev_cves,
      tone: summary.kev_cves > 0 ? 'critical' : 'good',
      title: 'Verschiedene aktiv ausgenutzte CVEs aus den in Dependency-Track aktivierten KEV-Quellen.',
    },
    {
      label: 'Offene Bewertungen',
      value: offeneBewertungen,
      tone: toneForCount(offeneBewertungen, 'warn'),
      title:
        'KEV-Treffer ohne Anwendbarkeits-Bewertung. Erst eine Bewertung „Betroffen“ gibt die Kundenbenachrichtigung frei.',
    },
  ];
}

/* ------------------------------------------------------------------------- *
 * Client-side filtering
 * ------------------------------------------------------------------------- */

/**
 * Split the free-text box into lower-cased tokens. All tokens must match (AND),
 * which makes `siemens cve-2024` a useful query instead of a dead end.
 */
export function searchTokens(input: string): string[] {
  return input
    .toLowerCase()
    .split(/\s+/)
    .map((token) => token.trim())
    .filter((token) => token !== '');
}

/** Everything the free-text filter searches in, lower-cased and joined. */
export function rowHaystack(row: OverviewRow): string {
  const parts: (string | null)[] = [
    row.kunde.name,
    row.kunde.email,
    row.produkttyp.bezeichnung,
    row.produkttyp.modellreihe,
    row.produkt.seriennummer,
    row.produkt.bezeichnung,
    row.produktversion.version_nr,
    row.produktversion.name,
    ...row.kev_cves,
  ];
  for (const komponente of row.komponenten) {
    parts.push(komponente.name, komponente.version, komponente.hersteller, ...komponente.kev_cves);
  }
  return parts
    .filter((part): part is string => typeof part === 'string' && part !== '')
    .join(' ')
    .toLowerCase();
}

/**
 * Client-side row filter. `onlyUnnotified` keeps the vanilla semantics: a row
 * without affected components is not "unnotified", it simply needs no notice.
 */
export function matchesFilters(
  row: OverviewRow,
  tokens: readonly string[],
  onlyUnnotified: boolean,
): boolean {
  if (onlyUnnotified && (row.notified || row.affected_count === 0)) return false;
  if (tokens.length === 0) return true;
  const haystack = rowHaystack(row);
  return tokens.every((token) => haystack.includes(token));
}

/* ------------------------------------------------------------------------- *
 * The notify gate — a CROSS-PAYLOAD JOIN
 * ------------------------------------------------------------------------- */

/**
 * What the "Benachrichtigung" column may offer for one row.
 *
 * `ready` is reachable ONLY through the vulnerability-events payload: an
 * `OverviewRow` knows that a component is KEV-affected, but not whether a human
 * decided the CVE is actually applicable. Both payloads must therefore be loaded
 * together — with an empty event list every affected row degrades to
 * `awaiting-bewertung`, which is exactly what the legacy page did.
 */
export type NotifyGate =
  /** No KEV hit at all — nothing to report. */
  | { readonly kind: 'clean' }
  /** At least one Benachrichtigung exists and none is still OFFEN. */
  | { readonly kind: 'notified'; readonly versendetAm: IsoDateTime | null; readonly openCount: number }
  /** KEV hit, but no event of this instance is BETROFFEN (yet). */
  | { readonly kind: 'awaiting-bewertung'; readonly openEvents: number; readonly totalEvents: number }
  /** Verdict BETROFFEN — the customer may be notified. */
  | { readonly kind: 'ready'; readonly openCount: number; readonly kundeResolvable: boolean };

/** Events whose fan-out touches this ProduktInstanz. */
export function eventsForRow(
  row: OverviewRow,
  events: readonly VulnerabilityEventRow[],
): VulnerabilityEventRow[] {
  return events.filter((event) =>
    event.betroffene.some((betroffen) => betroffen.instanz_id === row.produkt.id),
  );
}

/** Newest `versendet_am` of a row's Benachrichtigungen, or null. */
export function lastVersendetAm(
  benachrichtigungen: readonly BenachrichtigungBlock[],
): IsoDateTime | null {
  let latest: IsoDateTime | null = null;
  for (const block of benachrichtigungen) {
    const value = block.versendet_am;
    if (value === null || value === '') continue;
    // ISO-8601 from `_iso_or_none` — lexical comparison is chronological.
    if (latest === null || value > latest) latest = value;
  }
  return latest;
}

/** Benachrichtigung card ids of this row that are still OFFEN. */
export function openBenachrichtigungIds(row: OverviewRow): number[] {
  return row.benachrichtigungen
    .filter((block) => block.status === 'OFFEN' && block.id !== null)
    .map((block) => block.id as number);
}

/** Decide what the notify column may offer. See {@link NotifyGate}. */
export function notifyGate(row: OverviewRow, events: readonly VulnerabilityEventRow[]): NotifyGate {
  if (row.affected_count === 0) return { kind: 'clean' };
  if (row.notified) {
    return {
      kind: 'notified',
      versendetAm: lastVersendetAm(row.benachrichtigungen),
      openCount: openBenachrichtigungIds(row).length,
    };
  }
  const own = eventsForRow(row, events);
  if (!own.some((event) => event.bewertung === 'BETROFFEN')) {
    return {
      kind: 'awaiting-bewertung',
      openEvents: own.filter((event) => event.bewertung === 'OFFEN').length,
      totalEvents: own.length,
    };
  }
  return {
    kind: 'ready',
    openCount: row.benachrichtigungen.filter((block) => block.status === 'OFFEN').length,
    kundeResolvable: row.kunde.id !== null,
  };
}

/* ------------------------------------------------------------------------- *
 * Links
 * ------------------------------------------------------------------------- */

/**
 * Build the `mailto:` for a drafted advisory. The notify payload (unlike the
 * triage e-mail drafts) carries no ready-made link, so it is assembled here.
 * Returns null without a recipient — an empty `mailto:` opens a useless window.
 */
export function buildMailto(
  empfaenger: string | null,
  betreff: string,
  inhalt: string,
): string | null {
  const to = (empfaenger ?? '').trim();
  if (to === '') return null;
  const query = new URLSearchParams({ subject: betreff, body: inhalt }).toString();
  // URLSearchParams encodes spaces as '+', which mail clients show literally.
  return `mailto:${encodeURIComponent(to)}?${query.replace(/\+/g, '%20')}`;
}

/** German percent rendering for an EPSS probability (0…1). */
export function fmtPercent(value: number): string {
  return `${(value * 100).toLocaleString('de-DE', { maximumFractionDigits: 1 })} %`;
}
